import {
  Body2,
  H2,
  ScrollingText,
  ScrollingTextBody,
  ScrollingTextSidebar,
} from '@cointracker/styleguide';
import {
  body1,
  body2,
  h5,
} from '@cointracker/styleguide/src/typography/Typography.css';
import { REBRAND_URLS } from '@cointracker/ui';
import parse, {
  Element,
  domToReact,
  type DOMNode,
  type HTMLReactParserOptions,
} from 'html-react-parser';
import React from 'react';
import {
  getInstructionsHeading,
  hasAutomaticSync,
  hasCustomFileImport,
} from '../../../pages-react/IntegrationsPage/InstructionsContent/utils';
import {
  IntegrationCategories,
  type Integration,
} from '../../../pages-react/IntegrationsPage/IntegrationsList/IntegrationCard/utils';
import { postBody } from '../BlogPost/BlogPost.css';

export interface IntegrationInfoProps {
  integration: Integration;
}

export const IntegrationInfo = (props: IntegrationInfoProps) => {
  const { integration, ...rest } = props;

  const title = getInstructionsHeading(integration);

  return (
    <ScrollingText {...rest}>
      <ScrollingTextSidebar>
        <div className="mw-[300px] mt-8">
          <Body2>Need help?</Body2>
          <Body2 variant="secondary">
            Visit our{' '}
            <a href={REBRAND_URLS.SUPPORT} className="underline">
              help center
            </a>{' '}
            or{' '}
            <a href={REBRAND_URLS.CONTACT_US} className="underline">
              contact us
            </a>
            .
          </Body2>
        </div>
      </ScrollingTextSidebar>
      <ScrollingTextBody>
        <H2>{title}</H2>
        <div className={postBody}>
          <Instructions integration={integration} />
        </div>
      </ScrollingTextBody>
    </ScrollingText>
  );
};

interface InstructionsProps {
  integration: Integration;
}

const parseOptions: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (domNode instanceof Element && domNode.attribs) {
      const children = () =>
        domToReact(domNode.children as DOMNode[], parseOptions);

      switch (domNode.name) {
        case 'h2':
          return <h2 className={h5({})}>{children()}</h2>;
        case 'h3':
          return <h3 className={body1({ weight: 'bold' })}>{children()}</h3>;
        case 'p':
          return <p className={body2()}>{children()}</p>;
      }
    }
  },
};

const Instructions = (props: InstructionsProps) => {
  const { integration } = props;

  // Render description and instructions
  if (integration.description && integration.instructions) {
    return parse(integration.instructions, parseOptions);
  }

  // Render description
  if (integration.description) {
    return parse(integration.description, parseOptions);
  }

  // If not description, we should use instructions
  if (integration.instructions) {
    return parse(integration.instructions, parseOptions);
  }

  // Generic API instructions (if exchange)
  if (
    integration.category === IntegrationCategories.exchange &&
    hasAutomaticSync(integration)
  ) {
    return (
      <div>
        To automatically sync your {integration.name} account to CoinTracker:
        <ol className="my-14 list-outside list-decimal pl-40">
          <li>Create a new read-only API Key & Secret pair on Name</li>
          <li>
            Add your API Key & Secret pair to{' '}
            <a href={`/add_wallets?t=${integration.slug?.toLowerCase()}`}>
              CoinTracker here
            </a>
          </li>
        </ol>
      </div>
    );
  }

  // Generic CSV instructions
  if (hasCustomFileImport(integration)) {
    return (
      <div>
        To add your {integration.name} transactions to CoinTracker:
        <ol className="my-14 list-outside list-decimal pl-40">
          <li>
            Download the CSV export of your transactions from {integration.name}
          </li>
          <li>
            Import your CSV export to{' '}
            <a href={`/add_wallets?t=${integration.slug?.toLowerCase()}`}>
              CoinTracker here
            </a>
          </li>
        </ol>
      </div>
    );
  }

  // Instructions for Cointracker CSV
  const supportLink =
    'https://support.cointracker.io/hc/en-us/articles/4413071299729-Converting-transaction-history-CSVs-to-the-CoinTracker-CSV-format';
  return (
    <div>
      To add your {integration.name} transactions to CoinTracker:
      <ol className="my-14 list-outside list-decimal pl-40">
        <li>
          Download the CSV export of your transactions from {integration.name}
        </li>
        <li>
          Reformat those transactions into our CoinTracker CSV format (
          <a href={supportLink}>see our support guide here</a>)
        </li>
        <li>
          Import your CSV export to{' '}
          <a href={`/add_wallets?t=${integration.slug?.toLowerCase()}`}>
            CoinTracker here
          </a>
        </li>
      </ol>
    </div>
  );
};
